import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  CreditCard, Calendar, DollarSign, AlertCircle, CheckCircle, PauseCircle,
  PlayCircle, Clock, TrendingUp, Users, FileText, Settings, RefreshCw,
  Loader2, ChevronRight, BarChart3, Download, AlertTriangle, Pencil
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import EditorialPageHeader from '../components/EditorialPageHeader';

// Use API from App.js import

const EmbeddedLayout = ({ children }) => <>{children}</>;

export default function AdminPaymentPlans({ embedded = false }) {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [statusFilter, setStatusFilter] = useState('all');
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [lateFeeConfig, setLateFeeConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showModifyDialog, setShowModifyDialog] = useState(false);
  const [showStatementDialog, setShowStatementDialog] = useState(false);
  const [statement, setStatement] = useState(null);
  const [modifyData, setModifyData] = useState({ modifications: [] });
  const [actionLoading, setActionLoading] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchDashboard();
    fetchLateFeeConfig();
  }, [statusFilter]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [activeTab]);

  const fetchDashboard = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await axios.get(
        `${API}/subscriptions/payment-plans/dashboard?status_filter=${statusFilter}`,
        { headers }
      );
      setPlans(response.data.plans);
      setStats(response.data.stats);
    } catch (error) {
      setLoadError(error.response?.data?.detail || "We couldn't load payment plans. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(
        `${API}/subscriptions/payment-plans/analytics?days=30`,
        { headers }
      );
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    }
  };

  const fetchLateFeeConfig = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/late-fee-config`, { headers });
      setLateFeeConfig(response.data);
    } catch (error) {
      console.error('Failed to load late fee config');
    }
  };

  const handlePausePlan = async (registrationId) => {
    setActionLoading(registrationId);
    try {
      await axios.post(`${API}/subscriptions/payment-plan/${registrationId}/pause`, {}, { headers });
      toast.success('Payment plan paused');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause plan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumePlan = async (registrationId) => {
    setActionLoading(registrationId);
    try {
      await axios.post(`${API}/subscriptions/payment-plan/${registrationId}/resume`, {}, { headers });
      toast.success('Payment plan resumed');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume plan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleModifySchedule = async () => {
    if (!selectedPlan) return;
    try {
      await axios.put(
        `${API}/subscriptions/payment-plan/${selectedPlan.registration_id}/modify`,
        modifyData,
        { headers }
      );
      toast.success('Payment schedule updated');
      setShowModifyDialog(false);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to modify schedule');
    }
  };

  const handleViewStatement = async (registrationId) => {
    try {
      const response = await axios.get(
        `${API}/subscriptions/payment-plan/${registrationId}/statement`,
        { headers }
      );
      setStatement(response.data.statement);
      setShowStatementDialog(true);
    } catch (error) {
      toast.error('Failed to generate statement');
    }
  };

  const handleSaveLateFeeConfig = async () => {
    try {
      await axios.put(`${API}/subscriptions/late-fee-config`, lateFeeConfig, { headers });
      toast.success('Late fee settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700',
      overdue: 'bg-red-100 text-red-700',
      paused: 'bg-amber-100 text-amber-700',
      completed: 'bg-blue-100 text-blue-700'
    };
    const icons = {
      active: <CheckCircle className="w-3 h-3" />,
      overdue: <AlertCircle className="w-3 h-3" />,
      paused: <PauseCircle className="w-3 h-3" />,
      completed: <CheckCircle className="w-3 h-3" />
    };
    return (
      <Badge className={`${styles[status] || 'bg-gray-100 text-gray-700'} flex items-center gap-1`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const handleDownloadPDF = () => {
    if (!statement) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
      <head>
        <title>Payment Statement - ${statement.player?.name || 'Statement'}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 40px; color: #1e293b; }
          h1 { font-size: 24px; margin-bottom: 4px; }
          h2 { font-size: 18px; margin-top: 24px; margin-bottom: 12px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; }
          .subtitle { color: #64748b; font-size: 14px; margin-bottom: 24px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
          .grid-item label { font-size: 12px; color: #64748b; display: block; margin-bottom: 2px; }
          .grid-item p { font-weight: 600; margin: 0; }
          .summary { display: flex; justify-content: space-around; background: #f8fafc; border-radius: 8px; padding: 16px; margin-bottom: 24px; text-align: center; }
          .summary .label { font-size: 12px; color: #64748b; }
          .summary .value { font-size: 20px; font-weight: 700; }
          .summary .paid { color: #059669; }
          .summary .remaining { color: #d97706; }
          table { width: 100%; border-collapse: collapse; }
          th, td { text-align: left; padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
          th { font-weight: 600; color: #64748b; font-size: 12px; text-transform: uppercase; }
          .paid-badge { color: #059669; font-weight: 600; }
          .pending-badge { color: #64748b; }
          .footer { margin-top: 40px; font-size: 12px; color: #94a3b8; text-align: center; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>Payment Statement</h1>
        <p class="subtitle">Generated ${new Date().toLocaleDateString()}</p>
        <div class="grid">
          <div class="grid-item"><label>Organization</label><p>${statement.organization?.name || 'N/A'}</p></div>
          <div class="grid-item"><label>Program</label><p>${statement.program || 'N/A'}</p></div>
          <div class="grid-item"><label>Player</label><p>${statement.player?.name || 'N/A'}</p></div>
          <div class="grid-item"><label>Parent</label><p>${statement.parent?.name || 'N/A'}</p></div>
        </div>
        <div class="summary">
          <div><div class="label">Total</div><div class="value">$${statement.summary?.total_amount?.toFixed(2) || '0.00'}</div></div>
          <div><div class="label">Paid</div><div class="value paid">$${statement.summary?.paid_amount?.toFixed(2) || '0.00'}</div></div>
          <div><div class="label">Remaining</div><div class="value remaining">$${statement.summary?.outstanding?.toFixed(2) || '0.00'}</div></div>
        </div>
        <h2>Payment History</h2>
        <table>
          <thead><tr><th>#</th><th>Amount</th><th>Due Date</th><th>Status</th><th>Paid Date</th></tr></thead>
          <tbody>
            ${(statement.payments || []).map(p => `
              <tr>
                <td>${p.number}</td>
                <td>$${p.amount?.toFixed(2)}</td>
                <td>${p.due_date ? new Date(p.due_date).toLocaleDateString() : 'N/A'}</td>
                <td class="${p.status === 'paid' ? 'paid-badge' : 'pending-badge'}">${p.status === 'paid' ? 'Paid' : 'Pending'}</td>
                <td>${p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '—'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">This statement was generated automatically. For questions, contact your organization.</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  return (
    <PageLayout>
    <div className="space-y-6" data-testid="payment-plans-page">
      <EditorialPageHeader
        eyebrow="Finance"
        title="Payment Plans"
        description="Manage installment payment plans across registrations"
        headingId="payment-plans-heading"
        aside={<Button onClick={fetchDashboard} variant="outline" className="w-full gap-2 sm:w-auto">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" className="gap-2">
            <CreditCard className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="w-4 h-4" />
            Late Fees
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Collected</p>
                      <p className="text-xl font-bold">${stats.total_collected.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Outstanding</p>
                      <p className="text-xl font-bold">${stats.total_outstanding.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Overdue</p>
                      <p className="text-xl font-bold">{stats.overdue_plans}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Due in 7 Days</p>
                      <p className="text-xl font-bold">${stats.upcoming_amount_7_days.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-500">
              {plans.length} payment plan{plans.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Plans List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : loadError ? (
            <section className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-rose-50 p-6" role="alert">
              <h2 className="text-lg font-semibold text-rose-950">Payment plans unavailable</h2>
              <p className="mt-2 text-sm leading-6 text-rose-800">{loadError}</p>
              <Button onClick={fetchDashboard} className="mt-4 rounded-xl bg-rose-700 hover:bg-rose-800">Try again</Button>
            </section>
          ) : plans.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No payment plans found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {plans.map((plan) => (
                <Card key={plan.registration_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                      <div className="flex min-w-0 items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                          <Users className="w-6 h-6 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{plan.player_name}</p>
                          <p className="text-sm text-slate-500">{plan.program_name}</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 xl:justify-end xl:gap-6">
                        {/* Progress */}
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Progress</p>
                          <p className="font-medium">
                            {plan.paid_payments}/{plan.total_payments} payments
                          </p>
                          <div className="w-24 h-2 bg-slate-200 rounded-full mt-1">
                            <div 
                              className="h-2 bg-emerald-500 rounded-full"
                              style={{ width: `${(plan.paid_payments / plan.total_payments) * 100}%` }}
                            />
                          </div>
                        </div>
                        
                        {/* Amount */}
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Outstanding</p>
                          <p className="font-bold text-lg">${plan.outstanding.toFixed(2)}</p>
                        </div>
                        
                        {/* Status */}
                        {getStatusBadge(plan.status)}
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {plan.is_paused ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResumePlan(plan.registration_id)}
                              disabled={actionLoading === plan.registration_id}
                              className="gap-1"
                            >
                              {actionLoading === plan.registration_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <PlayCircle className="w-4 h-4" />
                              )}
                              Resume
                            </Button>
                          ) : plan.status !== 'completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePausePlan(plan.registration_id)}
                              disabled={actionLoading === plan.registration_id}
                              className="gap-1"
                            >
                              {actionLoading === plan.registration_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <PauseCircle className="w-4 h-4" />
                              )}
                              Pause
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewStatement(plan.registration_id)}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedPlan(plan);
                              setShowModifyDialog(true);
                            }}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          {analytics ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-slate-500">Success Rate</p>
                    <p className="text-2xl font-bold text-emerald-600">{analytics.summary.success_rate}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-slate-500">Collection Rate</p>
                    <p className="text-2xl font-bold">{analytics.summary.collection_rate}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-slate-500">Avg Collection Time</p>
                    <p className="text-2xl font-bold">{analytics.summary.avg_collection_days} days</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-slate-500">Failed Payments</p>
                    <p className="text-2xl font-bold text-red-600">{analytics.summary.failed_payments}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Failure Reasons */}
              {analytics.failure_reasons.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Failure Reasons</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analytics.failure_reasons.map((reason, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-slate-600">{reason.reason}</span>
                          <Badge variant="secondary">{reason.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Monthly Trend */}
              {analytics.monthly_trend.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Monthly Collections</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analytics.monthly_trend.map((month) => (
                        <div key={month.month} className="flex items-center gap-4">
                          <span className="w-20 text-sm text-slate-500">{month.month}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-4">
                            <div 
                              className="bg-emerald-500 h-4 rounded-full"
                              style={{ 
                                width: `${Math.min(100, (month.amount / (analytics.summary.total_collected / analytics.monthly_trend.length)) * 50)}%` 
                              }}
                            />
                          </div>
                          <span className="font-medium">${month.amount.toLocaleString()}</span>
                          <span className="text-sm text-slate-500">({month.count} payments)</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          )}
        </TabsContent>

        {/* Late Fee Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          {lateFeeConfig && (
            <Card>
              <CardHeader>
                <CardTitle>Late Fee Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable Late Fees</Label>
                    <p className="text-sm text-slate-500">Automatically apply late fees to overdue payments</p>
                  </div>
                  <Switch
                    checked={lateFeeConfig.enabled}
                    onCheckedChange={(checked) => setLateFeeConfig({ ...lateFeeConfig, enabled: checked })}
                  />
                </div>

                {lateFeeConfig.enabled && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Grace Period (Days)</Label>
                        <Input
                          type="number"
                          value={lateFeeConfig.grace_period_days}
                          onChange={(e) => setLateFeeConfig({ 
                            ...lateFeeConfig, 
                            grace_period_days: parseInt(e.target.value) 
                          })}
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">Days after due date before fee applies</p>
                      </div>
                      <div>
                        <Label>Fee Type</Label>
                        <Select
                          value={lateFeeConfig.late_fee_type}
                          onValueChange={(value) => setLateFeeConfig({ ...lateFeeConfig, late_fee_type: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">Fixed Amount</SelectItem>
                            <SelectItem value="percentage">Percentage</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {lateFeeConfig.late_fee_type === 'fixed' ? (
                        <div>
                          <Label>Late Fee Amount ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={lateFeeConfig.late_fee_amount}
                            onChange={(e) => setLateFeeConfig({ 
                              ...lateFeeConfig, 
                              late_fee_amount: parseFloat(e.target.value) 
                            })}
                            className="mt-1"
                          />
                        </div>
                      ) : (
                        <div>
                          <Label>Late Fee Percentage (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={lateFeeConfig.late_fee_percentage}
                            onChange={(e) => setLateFeeConfig({ 
                              ...lateFeeConfig, 
                              late_fee_percentage: parseFloat(e.target.value) 
                            })}
                            className="mt-1"
                          />
                        </div>
                      )}
                      <div>
                        <Label>Maximum Late Fee ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={lateFeeConfig.max_late_fee}
                          onChange={(e) => setLateFeeConfig({ 
                            ...lateFeeConfig, 
                            max_late_fee: parseFloat(e.target.value) 
                          })}
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">Cap on maximum late fee amount</p>
                      </div>
                    </div>
                  </>
                )}

                <Button onClick={handleSaveLateFeeConfig} className="gap-2">
                  <Settings className="w-4 h-4" />
                  Save Settings
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Statement Dialog */}
      <Dialog open={showStatementDialog} onOpenChange={setShowStatementDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payment Statement</DialogTitle>
          </DialogHeader>
          {statement && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Organization</p>
                  <p className="font-medium">{statement.organization.name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Program</p>
                  <p className="font-medium">{statement.program}</p>
                </div>
                <div>
                  <p className="text-slate-500">Player</p>
                  <p className="font-medium">{statement.player.name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Parent</p>
                  <p className="font-medium">{statement.parent.name}</p>
                </div>
              </div>

              <div className="border rounded-lg p-4 bg-slate-50">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-slate-500">Total</p>
                    <p className="text-xl font-bold">${statement.summary.total_amount.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Paid</p>
                    <p className="text-xl font-bold text-emerald-600">${statement.summary.paid_amount.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Remaining</p>
                    <p className="text-xl font-bold text-amber-600">${statement.summary.outstanding.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="font-medium">Payment History</p>
                {statement.payments.map((payment) => (
                  <div 
                    key={payment.number}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      payment.status === 'paid' ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        payment.status === 'paid' ? 'bg-emerald-500 text-white' : 'bg-slate-300 text-slate-600'
                      }`}>
                        {payment.status === 'paid' ? '✓' : payment.number}
                      </div>
                      <div>
                        <p className="font-medium">${payment.amount.toFixed(2)}</p>
                        <p className="text-xs text-slate-500">
                          {payment.status === 'paid' 
                            ? `Paid ${new Date(payment.paid_at).toLocaleDateString()}`
                            : `Due ${new Date(payment.due_date).toLocaleDateString()}`
                          }
                        </p>
                      </div>
                    </div>
                    {payment.status === 'paid' && (
                      <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatementDialog(false)}>Close</Button>
            <Button className="gap-2" onClick={handleDownloadPDF}>
              <Download className="w-4 h-4" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modify Schedule Dialog */}
      <Dialog open={showModifyDialog} onOpenChange={setShowModifyDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modify Payment Schedule</DialogTitle>
          </DialogHeader>
          {selectedPlan && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-700">
                  Changing installment amounts may affect the total balance. Review carefully before saving.
                </p>
              </div>

              <div className="text-sm text-slate-500 mb-2">
                <span className="font-medium text-slate-700">{selectedPlan.player_name}</span> — {selectedPlan.program_name}
              </div>

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {(selectedPlan.schedule || selectedPlan.payments || []).map((installment, idx) => {
                  const isPaid = installment.status === 'paid';
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        isPaid ? 'bg-emerald-50 border-emerald-200 opacity-60' : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
                        isPaid ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {isPaid ? '✓' : installment.number || idx + 1}
                      </div>

                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">Due Date</Label>
                          <Input
                            type="date"
                            value={installment.due_date ? installment.due_date.split('T')[0] : ''}
                            disabled={isPaid}
                            onChange={(e) => {
                              const updated = [...(selectedPlan.schedule || selectedPlan.payments || [])];
                              updated[idx] = { ...updated[idx], due_date: e.target.value };
                              setSelectedPlan({ ...selectedPlan, schedule: updated });
                              setModifyData({
                                modifications: updated
                                  .filter(inst => inst.status !== 'paid')
                                  .map(inst => ({
                                    installment_number: inst.number || inst.installment_number,
                                    due_date: inst.due_date,
                                    amount: inst.amount
                                  }))
                              });
                            }}
                            className="mt-1 h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Amount ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={installment.amount}
                            disabled={isPaid}
                            onChange={(e) => {
                              const updated = [...(selectedPlan.schedule || selectedPlan.payments || [])];
                              updated[idx] = { ...updated[idx], amount: parseFloat(e.target.value) || 0 };
                              setSelectedPlan({ ...selectedPlan, schedule: updated });
                              setModifyData({
                                modifications: updated
                                  .filter(inst => inst.status !== 'paid')
                                  .map(inst => ({
                                    installment_number: inst.number || inst.installment_number,
                                    due_date: inst.due_date,
                                    amount: inst.amount
                                  }))
                              });
                            }}
                            className="mt-1 h-8 text-sm"
                          />
                        </div>
                      </div>

                      {isPaid && (
                        <Badge className="bg-emerald-100 text-emerald-700 flex-shrink-0">Paid</Badge>
                      )}
                    </div>
                  );
                })}

                {(!selectedPlan.schedule && !selectedPlan.payments) && (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No schedule data available. Schedule details will load from the payment plan.
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModifyDialog(false)}>Cancel</Button>
            <Button onClick={handleModifySchedule}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageLayout>
  );
}
